import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import BrokerAccessTable from "./BrokerAccessTable";

const GiveBrokerAccess = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const handleClose = () => { setOpen(false); setPhone(""); };

  const handleSubmit = async () => {
    if (!phone.trim()) { toast.error("Please enter a phone number"); return; }
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "/appdashboard/give-broker-access/",
        data: { phone: phone.trim() },
      });
      const msg = res?.data?.message || res?.data?.response || "Broker access granted!";
      toast.success(msg);
      handleClose();
      setRefetchKey((k) => k + 1);
    } catch (e) {
      const errMsg =
        e.response?.data?.message ||
        e.response?.data?.response ||
        e.response?.data?.detail ||
        e.message ||
        "Failed to grant broker access";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3, py: 2, mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AccountBalanceIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Broker Access
            </Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              Manage broker integration access for users
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setOpen(true)}
          sx={{
            background: "#016551",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: "8px",
            textTransform: "none",
            px: 2.5,
            py: 1,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          Give Access
        </Button>
      </Box>

      <BrokerAccessTable refetchKey={refetchKey} />

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "14px", width: 420 } }}>
        <Box sx={{ px: 3, py: 2, background: "#04343a", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
            Give Broker Access
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: "#eef2f7", "&:hover": { color: "#fff" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              inputProps={{ maxLength: 15 }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14 },
                "& .MuiInputLabel-shrink": { backgroundColor: "#fff", px: 0.5, borderRadius: "2px" },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} size="small" sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={loading || !phone.trim()}
            startIcon={loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              px: 2.5,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&:disabled": { background: "#94a3b8", boxShadow: "none" },
            }}
          >
            {loading ? "Granting…" : "Grant Access"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GiveBrokerAccess;
